/**
 * Боковая панель поверх страницы — карточка, длинная форма, предпросмотр.
 *
 * Модальная по всем правилам: фокус уходит в панель и не выходит из неё по
 * Tab, Esc и щелчок по подложке закрывают, остальное приложение недоступно
 * (`inert` на #root — и для мыши, и для скринридера), после закрытия фокус
 * возвращается туда, откуда панель открыли. Ширина — min(размер, 100vw): на
 * телефоне панель во весь экран.
 *
 * Разметка внутри: `DrawerHeader` (не прокручивается), `DrawerBody`
 * (прокручивается), `DrawerFooter` (кнопки, всегда под рукой). Панель —
 * container `drawer` для container queries содержимого.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { focusables, setAppInert } from '@shared/lib/appInert';
import { IconX } from './icons';

/**
 * Держит подложку по видимой области: положение и размер visual viewport —
 * в CSS-переменных элемента (значения меняются при прокрутке, масштабе и
 * экранной клавиатуре, поэтому только так, а не стилями компонента).
 */
export function useVisualViewportBox(ref: RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    const vv = window.visualViewport;
    const el = ref.current;
    if (!vv || !el) return;
    const apply = () => {
      el.style.setProperty('--vv-left', `${vv.offsetLeft}px`);
      el.style.setProperty('--vv-top', `${vv.offsetTop}px`);
      el.style.setProperty('--vv-width', `${vv.width}px`);
      el.style.setProperty('--vv-height', `${vv.height}px`);
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
    };
  }, [ref]);
}

export type DrawerSize = 'md' | 'lg';

const WIDTH: Readonly<Record<DrawerSize, string>> = { md: '720px', lg: '980px' };

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(24px); }
  to   { opacity: 1; transform: translateX(0); }
`;

/**
 * Подложка совпадает с видимой областью (visual viewport), а не с layout
 * viewport: на телефоне, если что-то на странице шире экрана, Chrome
 * растягивает layout viewport до ширины содержимого, и `position: fixed;
 * inset: 0` уезжает за край. Размеры приходят из `useVisualViewportBox`
 * CSS-переменными; без них — обычный `inset: 0`.
 */
const Backdrop = styled.div`
  position: fixed;
  left: var(--vv-left, 0px);
  top: var(--vv-top, 0px);
  width: var(--vv-width, 100%);
  height: var(--vv-height, 100%);
  z-index: ${({ theme }) => theme.z.modal};
  background: ${({ theme }) => theme.colors.background}B8;
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  animation: ${fadeIn} ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};
`;

const Panel = styled.div<{ $size: DrawerSize }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(${({ $size }) => WIDTH[$size]}, 100%);
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  container: drawer / inline-size;
  animation: ${slideIn} ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};

  /* Фокус на самой панели — точка входа для Tab, рамка вокруг всего экрана
     ничего не сообщает. Элементы внутри показывают фокус как везде. */
  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.lg};
  }
`;

export const DrawerHeader = styled.header`
  flex: none;
  padding: ${({ theme }) => `${theme.spacing(5)} ${theme.spacing(6)} 0`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  @container drawer (max-width: 520px) {
    padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(4)} 0`};
  }
`;

export const DrawerBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scroll-behavior: smooth;
  padding: ${({ theme }) => `${theme.spacing(5)} ${theme.spacing(6)} ${theme.spacing(10)}`};

  @container drawer (max-width: 520px) {
    padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(4)} ${theme.spacing(8)}`};
  }
`;

export const DrawerFooter = styled.footer`
  flex: none;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(6)}`};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};

  @container drawer (max-width: 520px) {
    padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  }
`;

/** Квадратная кнопка-иконка шапки панели: закрыть, предыдущая, следующая. */
export const DrawerIconButton = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radius.md};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:disabled {
    cursor: default;
    color: ${({ theme }) => theme.colors.text.muted};
    border-color: ${({ theme }) => theme.colors.border};
  }
`;

export function DrawerCloseButton({ onClick }: { onClick: () => void }) {
  return (
    <DrawerIconButton type="button" onClick={onClick} aria-label="Закрыть" title="Закрыть (Esc)">
      <IconX size={18} />
    </DrawerIconButton>
  );
}

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** id заголовка внутри панели — имя диалога для скринридера. */
  labelledBy: string;
  size?: DrawerSize;
  /** Что получит фокус при открытии; по умолчанию — сама панель. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  children: ReactNode;
}

export function Drawer({ open, ...props }: DrawerProps) {
  if (!open) return null;
  return createPortal(<DrawerPanel {...props} />, document.body);
}

function DrawerPanel({
  onClose,
  labelledBy,
  size = 'md',
  initialFocusRef,
  children,
}: Omit<DrawerProps, 'open'>) {
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  useVisualViewportBox(backdropRef);

  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  // Только при открытии: запомнить, откуда пришли, и увести фокус в панель.
  // initialFocusRef читается один раз — дальше фокусом управляет человек.
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setAppInert(true);
    (initialFocusRef?.current ?? panelRef.current)?.focus({ preventScroll: true });
    return () => {
      setAppInert(false);
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);

  // Фокус мог «упасть» на body — элемент, где он стоял, исчез (кнопку заменило
  // подтверждение). Тогда Tab возвращает в панель, Esc по-прежнему закрывает.
  useEffect(() => {
    const onDocumentKey = (e: globalThis.KeyboardEvent) => {
      const panel = panelRef.current;
      if (!panel || panel.contains(document.activeElement)) return;
      if (e.key === 'Escape') {
        closeRef.current();
      } else if (e.key === 'Tab') {
        e.preventDefault();
        (focusables(panel)[0] ?? panel).focus();
      }
    };
    document.addEventListener('keydown', onDocumentKey);
    return () => document.removeEventListener('keydown', onDocumentKey);
  }, []);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.defaultPrevented) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      closeRef.current();
      return;
    }
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const items = focusables(panel);
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) {
      e.preventDefault();
      panel.focus();
      return;
    }
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <Backdrop
      ref={backdropRef}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeRef.current();
      }}
    >
      <Panel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        $size={size}
        onKeyDown={onKeyDown}
      >
        {children}
      </Panel>
    </Backdrop>
  );
}
